/**
 * KPI card with optional sparkline.
 *
 * Pure visual — pass:
 *   label   — small kicker
 *   value   — primary big number
 *   sub     — optional secondary line
 *   spark   — array<number> sparkline series
 *   icon    — lucide icon node
 *   tone    — gold | green | red | slate
 *   delta   — optional EX-B PoP indicator:
 *             { direction: 'up' | 'down' | 'flat', label: '+12.4%' | '-3.1%' | '0.0%' | 'new', subtitle: 'vs prev 7d' }
 *             Renders a coloured badge under the primary value. Pre-EX-B
 *             consumers that omit this prop see no badge — byte-identical.
 */
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { classes } from '@/lib/utils';

const TONE = {
  gold:  { ring: 'border-gold/15', accent: '#C9A84C' },
  green: { ring: 'border-emerald-500/15', accent: '#34D399' },
  red:   { ring: 'border-red-500/15', accent: '#F87171' },
  slate: { ring: 'border-white/8', accent: '#8A8A8A' },
};

// EX-B Step 5 — PoP badge palette (tailwind classes only — no new colors).
const DELTA_STYLE = {
  up:   { wrap: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', Icon: ArrowUpRight },
  down: { wrap: 'text-rose-400 bg-rose-500/10 border-rose-500/20',          Icon: ArrowDownRight },
  flat: { wrap: 'text-white/55 bg-white/5 border-white/10',                  Icon: Minus },
};

export default function KpiCard({
  label, value, sub, spark, icon, tone = 'gold', testID, delta,
}) {
  const t = TONE[tone] || TONE.gold;
  const seriesData = (spark || []).map((v, i) => ({ i, v: Number(v) || 0 }));
  const showSpark = seriesData.length > 1;
  const deltaStyle = delta ? DELTA_STYLE[delta.direction] || DELTA_STYLE.flat : null;
  const DeltaIcon = deltaStyle?.Icon;

  return (
    <div
      data-testid={testID}
      className={classes(
        'relative rounded-2xl bg-[#1A1A1A] border p-5 flex flex-col gap-3 overflow-hidden',
        'transition-all hover:border-gold/25',
        t.ring,
      )}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-white/50">
          {label}
        </div>
        {icon ? (
          <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 grid place-items-center text-gold">
            {icon}
          </div>
        ) : null}
      </div>
      <div className="text-3xl font-display font-bold tracking-tight leading-none">
        {value}
      </div>
      {sub ? <div className="text-xs text-white/45">{sub}</div> : null}
      {delta && deltaStyle ? (
        <div
          className={classes(
            'inline-flex items-center gap-1 self-start rounded-full border px-2 py-0.5 text-[11px] font-semibold',
            deltaStyle.wrap,
          )}
          data-testid={`${testID || 'kpi'}-delta`}
        >
          <DeltaIcon size={11} />
          <span>{delta.label}</span>
          {delta.subtitle ? (
            <span className="text-white/40 font-normal ml-1">{delta.subtitle}</span>
          ) : null}
        </div>
      ) : null}
      {showSpark ? (
        <div className="h-10 -mx-1 mt-auto">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={seriesData} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
              <Line
                type="monotone"
                dataKey="v"
                stroke={t.accent}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}
